import {
  schoolBoards as boards,
  courses,
  degreeTypes,
  institutes,
} from "../../../data/educationData";
import {
  employmentTypes,
  designations,
  departments,
  sectors,
  companyNames,
} from "../../../data/workData";
import EducationFields from "../../../components/FormComponents/EducationFields";
import WorkFields from "../../../components/FormComponents/WorkFields";
import IDCardFields from "../../../components/FormComponents/IDCardFields";
import BankDetailsFields from "../../../components/FormComponents/BankDetailsFields";
import SocialLinksFields from "../../../components/FormComponents/SocialLinksFields";
import AddressFields from "../../../components/FormComponents/AddressFields";
import React, { useState, useRef, useEffect, useMemo, useCallback } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Grid,
  Autocomplete,
  IconButton,
  Box,
  Typography,
  Avatar,
  FormControl,
  FormHelperText,
  Checkbox,
  FormControlLabel,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import CameraAltIcon from "@mui/icons-material/CameraAlt";
import Webcam from "react-webcam";
import PhoneInput from "react-phone-number-input";
import "react-phone-number-input/style.css";
import PhoneCountryAutocomplete from "../../FamilyTree/components/PhoneCountryAutocomplete";
import PincodeAutocomplete from "../../FamilyTree/components/PincodeAutocomplete";
import { CITIES } from "../../../data/cities";
import { LANGUAGES } from "../../../data/languages";
import { Country, State } from "country-state-city";
import Male from "../../../assets/images/male.png";
import Female from "../../../assets/images/female.png";
import { fetchMasterData } from "../../../services/masterDataService";
import { fetchEducationDetailsByParent, deleteEducationData } from "../../../services/educationService";
import { fetchWorkDetailsByParent, deleteWorkData } from "../../../services/workService";
import { fetchIDCardDetailsByParent, deleteIDCardData } from "../../../services/idCardService";
import { fetchBankDetailsByParent, deleteBankDetailsData } from "../../../services/bankDetailsService";
import { fetchSocialLinksByParent } from "../../../services/socialLinksService";
import { fetchAddressDetailsByParent, addAddressData, editAddressData, deleteAddressData } from "../../../services/addressService";
import { useSelector } from "react-redux";
import AddMasterDataDialog from "../../../components/AddMasterDataDialog";
import { useSocialLinksSections } from "../../../hooks/useSocialLinksSections";
import { usePincodeAutofill } from "../../../hooks/usePincodeAutofill";

// Helper function to decode HTML entities
const decodeHTMLEntities = (text) => {
  if (!text) return text;
  const textArea = document.createElement("textarea");
  textArea.innerHTML = text;
  return textArea.value;
};

const EditProfileDialog = ({
  open,
  onClose,
  profile,
  onSubmit,
  isSubmitting,
  error,
  educationDetails: _unusedEducationDetails = [],
}) => {
  const [formData, setFormData] = useState({});
  const [profilePhoto, setProfilePhoto] = useState(null);
  const [photoRemoved, setPhotoRemoved] = useState(false);
  const [hadPhotoBeforeRemoval, setHadPhotoBeforeRemoval] = useState(false);
  const [mobileError, setMobileError] = useState(false);
  const [touchedFields, setTouchedFields] = useState({});
  const [phoneCountry, setPhoneCountry] = useState("IN");
  const [cameraDialogOpen, setCameraDialogOpen] = useState(false);
  const [visibleEducationSections, setVisibleEducationSections] = useState(1);
  const [educationSectionError, setEducationSectionError] = useState("");
  const [educationEntries, setEducationEntries] = useState([]);
  const [educationFetchError, setEducationFetchError] = useState("");
  const [removedEducationIds, setRemovedEducationIds] = useState([]);
  const [educationFieldErrors, setEducationFieldErrors] = useState({});
  const [visibleWorkSections, setVisibleWorkSections] = useState(1);
  const [workSectionError, setWorkSectionError] = useState("");
  const [workEntries, setWorkEntries] = useState([]);
  const [workFetchError, setWorkFetchError] = useState("");
  const [removedWorkIds, setRemovedWorkIds] = useState([]);
  const [workFieldErrors, setWorkFieldErrors] = useState({});
  const [visibleIDCardSections, setVisibleIDCardSections] = useState(1);
  const [idCardSectionError, setIDCardSectionError] = useState("");
  const [idCardEntries, setIDCardEntries] = useState([]);
  const [idCardFetchError, setIDCardFetchError] = useState("");
  const [removedIDCardIds, setRemovedIDCardIds] = useState([]);
  const [idCardFieldErrors, setIDCardFieldErrors] = useState({});
  const [visibleBankSections, setVisibleBankSections] = useState(1);
  const [bankSectionError, setBankSectionError] = useState("");
  const [bankEntries, setBankEntries] = useState([]);
  const [bankFetchError, setBankFetchError] = useState("");
  const [removedBankIds, setRemovedBankIds] = useState([]);
  const [bankFieldErrors, setBankFieldErrors] = useState({});
  const [socialLinksFetchError, setSocialLinksFetchError] = useState("");
  const [addressFetchError, setAddressFetchError] = useState("");
  const [currentAddressData, setCurrentAddressData] = useState({});
  const [permanentAddressData, setPermanentAddressData] = useState({});
  const [sameAsCurrent, setSameAsCurrent] = useState(false);
  const [disabilityValidationError, setDisabilityValidationError] = useState("");
  const fileInputRef = useRef(null);
  const webcamRef = useRef(null);

  // Get sessionId from Redux (needed by hooks)
  const sessionId = useSelector((state) => state.session.sessionId);

  // Constants needed by hooks (must be declared before hook calls)
  const maxSocialLinksSections = 10;
  const socialLinksFieldKeys = [
    "socialLinksEntryId",
    "socialtype",
    "sociallink",
  ];

  // Use custom hook for social links management
  const {
    visibleSections: visibleSocialLinksSections,
    setVisibleSections: setVisibleSocialLinksSections,
    sectionError: socialLinksSectionError,
    setSectionError: setSocialLinksSectionError,
    fieldErrors: socialLinksFieldErrors,
    setFieldErrors: setSocialLinksFieldErrors,
    entries: socialLinksEntries,
    setEntries: setSocialLinksEntries,
    removedIds: removedSocialLinksIds,
    setRemovedIds: setRemovedSocialLinksIds,
    computeVisibleSections: computeVisibleSocialLinksFromHook,
    handleAddSection: handleAddSocialLinksSection,
    handleRemoveSection: handleRemoveSocialLinksSection,
  } = useSocialLinksSections(formData, setFormData, sessionId, maxSocialLinksSections);

  // Use custom hook for pincode autofill
  const { 
    handlePincodeChange: handlePincodeInput, 
    loading: pincodeLoading, 
    error: pincodeError,
    locationData,
    getVillageOptions
  } = usePincodeAutofill(setFormData);

  // Get village options from pincode data
  const villageOptions = locationData?.allAreas || [];

  // Death pincode autofill hook with custom field mapping for death location
  const {
    handlePincodeChange: handleDeathPincodeInput,
    loading: deathPincodeLoading,
    error: deathPincodeError,
    locationData: deathLocationData,
  } = usePincodeAutofill(setFormData, {
    pincode: 'deathpincode_c',
    city: 'deathcity_c',
    state: 'deathstate_c',
    country: 'deathcountry_c',
    area: 'deatharea_c',
  });

  // Get death area options from death pincode data
  const deathAreaOptions = deathLocationData?.allAreas || [];

  const maxEducationSections = 10;
  const educationFieldKeys = [
    "educationEntryId",
    "qualification",
    "education",
    "educationtype",
    "instituteschool",
    "institute",
    "board",
    "course",
    "degreetype",
    "startyear_c",
    "endyear_c",
    "currentlystudying_c",
    "grade",
    "instituecity",
    "institutestate",
    "institutecountry",
    "institutepincode",
    "institutetype",
    "description",
    "educationname",
    "yearofpassing",
  ];

  const maxWorkSections = 5;
  const workFieldKeys = [
    "workEntryId",
    "companyname",
    "designation",
    "department",
    "employmenttype",
    "sector",
    "startdate",
    "enddate",
    "currentlyworking",
    "description",
    "address",
    "country",
    "state",
    "city",
    "pincode",
    "workimage",
  ];

  const maxIDCardSections = 10;
  const idCardFieldKeys = [
    "idCardEntryId",
    "idtype",
    "idcardname",
    "idnumber",
    "issuedate",
    "expirydate",
    "idcarddescription",
  ];

  const maxBankSections = 10;
  const bankFieldKeys = [
    "bankEntryId",
    "accountholdername",
    "accountnumber",
    "ifsc",
    "accounttype",
    "bankname",
    "branch",
    "bankcountry",
    "status",
    "bankdescription",
    "image",
  ];

  const computeVisibleSections = useCallback(
    (data) => {
      let maxFilledSection = 0;
      for (let i = 1; i <= maxEducationSections; i++) {
        const hasData = [
          data[`qualification${i}`],
          data[`board${i}`],
          data[`course${i}`],
          data[`educationtype${i}`],
          data[`instituteschool${i}`],
          data[`institutecountry${i}`],
        ].some((value) => {
          if (typeof value === "string") {
            return value.trim() !== "";
          }
          return value !== undefined && value !== null && value !== "";
        });

        if (hasData) {
          maxFilledSection = i;
        }
      }

      return Math.max(1, maxFilledSection);
    },
    [maxEducationSections]
  );

  const computeVisibleWorkSections = useCallback(
    (data) => {
      let maxFilledSection = 0;
      for (let i = 1; i <= maxWorkSections; i++) {
        const hasData = [
          data[`companyname${i}`],
          data[`designation${i}`],
          data[`department${i}`],
          data[`employmenttype${i}`],
          data[`startdate${i}`],
        ].some((value) => {
          if (typeof value === "string") {
            return value.trim() !== "";
          }
          return value !== undefined && value !== null && value !== "";
        });

        if (hasData) {
          maxFilledSection = i;
        }
      }

      return Math.max(1, maxFilledSection);
    },
    [maxWorkSections]
  );

  const computeVisibleIDCardSections = useCallback(
    (data) => {
      let maxFilledSection = 0;
      for (let i = 1; i <= maxIDCardSections; i++) {
        const hasData = [
          data[`idtype${i}`],
          data[`idcardname${i}`],
          data[`idnumber${i}`],
          data[`issuedate${i}`],
        ].some((value) => {
          if (typeof value === "string") {
            return value.trim() !== "";
          }
          return value !== undefined && value !== null && value !== "";
        });

        if (hasData) {
          maxFilledSection = i;
        }
      }

      return Math.max(1, maxFilledSection);
    },
    [maxIDCardSections]
  );

  const computeVisibleBankSections = useCallback(
    (data) => {
      let maxFilledSection = 0;
      for (let i = 1; i <= maxBankSections; i++) {
        const hasData = [
          data[`accountholdername${i}`],
          data[`accountnumber${i}`],
          data[`ifsc${i}`],
          data[`bankname${i}`],
        ].some((value) => {
          if (typeof value === "string") {
            return value.trim() !== "";
          }
          return value !== undefined && value !== null && value !== "";
        });

        if (hasData) {
          maxFilledSection = i;
        }
      }

      return Math.max(1, maxFilledSection);
    },
    [maxBankSections]
  );

  // Get user photo from Redux
  const userPhoto = useSelector((state) =>
    state.session.setUserPhoto ? state.session.setUserPhoto : null
  );

  const userId = profile?.id?.value || profile?.id || "";

  const [castes, setCastes] = useState([]);
  const [religions, setReligions] = useState([]);

  // Fetch master data when component mounts or sessionId changes
  useEffect(() => {
    if (!sessionId) return;

    const loadMasterData = async () => {
      try {
        const data = await fetchMasterData(sessionId);
        setCastes(data.castes);
        setReligions(data.religions);
      } catch (masterError) {
        console.error("Error loading master data:", masterError);
        setCastes([]);
        setReligions([]);
      }
    };

    loadMasterData();
  }, [sessionId]);

  useEffect(() => {
    const fetchEducationDetails = async () => {
      if (!open || !sessionId || !userId) {
        setEducationEntries([]);
        setEducationFetchError("");
        setRemovedEducationIds([]);
        return;
      }

      try {
        setEducationFetchError("");
        const entries = await fetchEducationDetailsByParent(sessionId, userId);
        setEducationEntries(entries || []);
        setRemovedEducationIds([]);
      } catch (fetchError) {
        console.error("Error fetching education entries:", fetchError);
        setEducationFetchError("Unable to load education details from the server.");
        setEducationEntries([]);
        setRemovedEducationIds([]);
      }
    };

    fetchEducationDetails();
  }, [open, sessionId, userId]);

  useEffect(() => {
    const fetchWorkDetails = async () => {
      if (!open || !sessionId || !userId) {
        setWorkEntries([]);
        setWorkFetchError("");
        setRemovedWorkIds([]);
        return;
      }

      try {
        setWorkFetchError("");
        const entries = await fetchWorkDetailsByParent(sessionId, userId);
        setWorkEntries(entries || []);
        setRemovedWorkIds([]);
      } catch (fetchError) {
        console.error("Error fetching work entries:", fetchError);
        setWorkFetchError("Unable to load work experience details from the server.");
        setWorkEntries([]);
        setRemovedWorkIds([]);
      }
    };

    fetchWorkDetails();
  }, [open, sessionId, userId]);

  useEffect(() => {
    const fetchIDCardDetails = async () => {
      if (!open || !sessionId || !userId) {
        setIDCardEntries([]);
        setIDCardFetchError("");
        setRemovedIDCardIds([]);
        return;
      }

      try {
        setIDCardFetchError("");
        const entries = await fetchIDCardDetailsByParent(sessionId, userId);
        setIDCardEntries(entries || []);
        setRemovedIDCardIds([]);
      } catch (fetchError) {
        console.error("Error fetching ID card entries:", fetchError);
        setIDCardFetchError("Unable to load ID card details from the server.");
        setIDCardEntries([]);
        setRemovedIDCardIds([]);
      }
    };

    fetchIDCardDetails();
  }, [open, sessionId, userId]);

  useEffect(() => {
    const fetchBankDetails = async () => {
      if (!open || !sessionId || !userId) {
        setBankEntries([]);
        setBankFetchError("");
        setRemovedBankIds([]);
        return;
      }

      try {
        setBankFetchError("");
        const entries = await fetchBankDetailsByParent(sessionId, userId);
        setBankEntries(entries || []);
        setRemovedBankIds([]);
      } catch (fetchError) {
        console.error("Error fetching bank details entries:", fetchError);
        setBankFetchError("Unable to load bank details from the server.");
        setBankEntries([]);
        setRemovedBankIds([]);
      }
    };

    fetchBankDetails();
  }, [open, sessionId, userId]);

  useEffect(() => {
    const fetchSocialLinks = async () => {
      if (!open || !sessionId || !userId) {
        setSocialLinksEntries([]);
        setSocialLinksFetchError("");
        setRemovedSocialLinksIds([]);
        return;
      }

      try {
        setSocialLinksFetchError("");
        const entries = await fetchSocialLinksByParent(sessionId, userId);
        setSocialLinksEntries(entries || []);
        setRemovedSocialLinksIds([]);
      } catch (fetchError) {
        console.error("Error fetching social links entries:", fetchError);
        setSocialLinksFetchError("Unable to load social links from the server.");
        setSocialLinksEntries([]);
        setRemovedSocialLinksIds([]);
      }
    };

    fetchSocialLinks();
  }, [open, sessionId, userId]);

  useEffect(() => {
    const fetchAddressDetails = async () => {
      if (!open || !sessionId || !userId) {
        setCurrentAddressData({});
        setPermanentAddressData({});
        setAddressFetchError("");
        setSameAsCurrent(false);
        return;
      }

      try {
        setAddressFetchError("");
        const entries = await fetchAddressDetailsByParent(sessionId, userId);
        console.log("🏠 [ADDRESS] Fetched entries:", entries);
        
        // Separate Current and Permanent addresses
        // If there are duplicates, get the most recent one (last in array)
        const currentAddresses = entries.filter(entry => entry.addresstype_c === "Current");
        const permanentAddresses = entries.filter(entry => entry.addresstype_c === "Permanent");
        
        const currentAddr = currentAddresses.length > 0 ? currentAddresses[currentAddresses.length - 1] : {};
        const permanentAddr = permanentAddresses.length > 0 ? permanentAddresses[permanentAddresses.length - 1] : {};
        
        console.log("🏠 [ADDRESS] Current addresses found:", currentAddresses.length);
        console.log("🏠 [ADDRESS] Current address (latest):", currentAddr);
        console.log("🏠 [ADDRESS] Permanent addresses found:", permanentAddresses.length);
        console.log("🏠 [ADDRESS] Permanent address (latest):", permanentAddr);
        
        setCurrentAddressData(currentAddr);
        setPermanentAddressData(permanentAddr);
        
        // Check if permanent address is same as current
        if (currentAddr.id && permanentAddr.id) {
          const fieldsMatch = ['address', 'street', 'pincode', 'village', 'taluk', 'district', 'state', 'country'].every(
            field => currentAddr[field] === permanentAddr[field]
          );
          setSameAsCurrent(fieldsMatch);
        }
      } catch (fetchError) {
        console.error("Error fetching address details:", fetchError);
        setAddressFetchError("Unable to load address details from the server.");
        setCurrentAddressData({});
        setPermanentAddressData({});
        setSameAsCurrent(false);
      }
    };

    fetchAddressDetails();
  }, [open, sessionId, userId]);

  const countries = Country.getAllCountries();
  const states = State.getAllStates();

  const phoneLabels = {
    country: "Country",
    phone: "Phone",
  };

  useEffect(() => {
    if (profile && open) {
      console.log("🔄 [FORMDATA] useEffect triggered - Populating formData");
      console.log("🔄 [FORMDATA] currentAddressData at time of population:", currentAddressData);
      console.log("🔄 [FORMDATA] permanentAddressData at time of population:", permanentAddressData);
      
      const initialPhoto = userPhoto || null;
      setProfilePhoto(initialPhoto);
      setHadPhotoBeforeRemoval(!!initialPhoto);
      setPhotoRemoved(false);

      const countryValue = profile.address_country?.value || "";
      const stateValue = profile.address_state?.value || "";

      const countryObj =
        countries.find((c) => c.name === countryValue || c.isoCode === countryValue) || null;
      const stateObj =
        states.find((s) => s.name === stateValue || s.isoCode === stateValue) || null;

      const baseData = {
        firstName: profile.first_name?.value || "",
        lastName: profile.last_name?.value || "",
        dob: profile.dob_c?.value || "",
        email: profile.email_c?.value || "",
        mobile: profile.phone_mobile?.value || "",
        street: profile.address_street?.value || "",
        description: profile.selfdescription_c?.value || "",
        aadhaar: profile.aadhaar_c?.value || "",
        caste: profile.caste_c?.value || "",
        religion: profile.religion_c?.value || "",
        age: profile.age_c?.value || "",
        gender: profile.gender_c?.value || "",
        bloodgroup: profile.bloodgroup_c?.value || "",
        birthplace: profile.birthplace_c?.value || "",
        disability_c: profile.disability_c?.value || "",
        disabilitytype: profile.disabilitytype_c?.value || "",
        disabilitytype_c: profile.disabilitytype_c?.value || "",
        alivestatus: profile.alivestatus_c?.value || "",
        mothertongue: profile.mothertongue_c?.value || "",
        languagesknown: profile.languagesknown_c?.value || "",
        maritalstatus: profile.maritalstatus_c?.value || "",
        marriagedate: profile.marriagedate_c?.value || "",
        marriageplace: profile.marriageplace_c?.value || "",
        economical_status: profile.economical_status_c?.value || "",
  education1: "",
  education2: "",
  education3: "",
  education4: "",
  education5: "",
  education6: "",
  education7: "",
  education8: "",
  education9: "",
  education10: "",
  educationtype1: "",
  educationtype2: "",
  educationtype3: "",
  educationtype4: "",
  educationtype5: "",
  educationtype6: "",
  educationtype7: "",
  educationtype8: "",
  educationtype9: "",
  educationtype10: "",
  institute1: "",
  institute2: "",
  institute3: "",
  institute4: "",
  institute5: "",
  institute6: "",
  institute7: "",
  institute8: "",
  institute9: "",
  institute10: "",
  yearofpassing1: "",
  yearofpassing2: "",
  yearofpassing3: "",
  yearofpassing4: "",
  yearofpassing5: "",
  yearofpassing6: "",
  yearofpassing7: "",
  yearofpassing8: "",
  yearofpassing9: "",
  yearofpassing10: "",
        company: profile.company_c?.value || "",
        occupation: profile.occupation_c?.value || "",
        employmenttype: profile.employmenttype_c?.value || "",
        worklocation: profile.worklocation_c?.value || "",
        instagram: profile.instagram_c?.value || "",
        facebook: profile.facebook_c?.value || "",
        youtube: profile.youtube_c?.value || "",
        linkedin: profile.linkedin_c?.value || "",
        threads: profile.threads_c?.value || "",
        deathaddress_c: profile.deathaddress_c?.value || "",
        deatharea_c: profile.deatharea_c?.value || "",
        deathpincode_c: profile.deathpincode_c?.value || "",
        deathcity_c: profile.deathcity_c?.value || "",
        deathstate_c:
          states.find(
            (s) => s.name === profile.deathstate_c?.value || s.isoCode === profile.deathstate_c?.value
          ) || null,
        deathcountry_c:
          countries.find(
            (c) => c.name === profile.deathcountry_c?.value || c.isoCode === profile.deathcountry_c?.value
          ) || null,
        deathplace_c: profile.deathplace_c?.value || "",
        deathreason_c: profile.deathreason_c?.value || "",
        dateofdeath_c: profile.dateofdeath_c?.value || "",
      };

      const sourceEducation = Array.isArray(educationEntries) ? educationEntries : [];

      sourceEducation.slice(0, maxEducationSections).forEach((entry, index) => {
        if (!entry || typeof entry !== "object") return;

        const section = index + 1;
        const qualification = decodeHTMLEntities(entry.qualification || entry.name || "");
        const educationType = decodeHTMLEntities(entry.educationtype || "");
        const institute = decodeHTMLEntities(entry.institute || "");
        const board = decodeHTMLEntities(entry.board || "");
        const course = decodeHTMLEntities(entry.course || "");
        const degreetype = decodeHTMLEntities(entry.degreetype || "");
        const startyear_c = entry.startyear_c || "";
        const endyear_c = entry.endyear_c || "";
        const currentlystudying_c = entry.currentlystudying_c || false;
        const grade = decodeHTMLEntities(entry.grade || "");
        const instituecity = decodeHTMLEntities(entry.instituecity || "");
        const institutestate = decodeHTMLEntities(entry.institutestate || "");
        const institutecountry = decodeHTMLEntities(entry.institutecountry || "");
        const institutepincode = entry.institutepincode || "";
        const institutetype = decodeHTMLEntities(entry.institutetype || "");
        const description = decodeHTMLEntities(entry.description || "");

        baseData[`educationEntryId${section}`] = entry.id || "";
        baseData[`board${section}`] = board;
        baseData[`course${section}`] = course;
        baseData[`degreetype${section}`] = degreetype;
        baseData[`startyear_c${section}`] = startyear_c;
        baseData[`endyear_c${section}`] = endyear_c;
        baseData[`currentlystudying_c${section}`] = currentlystudying_c;
        baseData[`grade${section}`] = grade;
        baseData[`instituecity${section}`] = instituecity;
        baseData[`institutestate${section}`] = institutestate;
        baseData[`institutecountry${section}`] = institutecountry;
        baseData[`institutepincode${section}`] = institutepincode;
        baseData[`institutetype${section}`] = institutetype;
        baseData[`description${section}`] = description;
        baseData[`education${section}`] = qualification || baseData[`education${section}`] || "";
        baseData[`qualification${section}`] = qualification;
        baseData[`educationtype${section}`] =
          educationType || baseData[`educationtype${section}`] || "";
        baseData[`institute${section}`] = institute || baseData[`institute${section}`] || "";
        baseData[`instituteschool${section}`] =
          institute || baseData[`instituteschool${section}`] || "";
      });

      const sourceWork = Array.isArray(workEntries) ? workEntries : [];

      sourceWork.slice(0, maxWorkSections).forEach((entry, index) => {
        if (!entry || typeof entry !== "object") return;

        const section = index + 1;
        const companyname = decodeHTMLEntities(entry.companyname || "");
        const designation = decodeHTMLEntities(entry.designation || "");
        const department = decodeHTMLEntities(entry.department || "");
        const employmenttype = decodeHTMLEntities(entry.employmenttype || "");
        const sector = decodeHTMLEntities(entry.sector || "");
        const startdate = entry.startdate || "";
        const enddate = entry.enddate || "";
        // Properly convert currentlyworking to boolean
        const currentlyworking = entry.currentlyworking === true || entry.currentlyworking === 1 || entry.currentlyworking === "1" || entry.currentlyworking === "true";
        const description = decodeHTMLEntities(entry.description || "");
        const address = decodeHTMLEntities(entry.address || "");
        const country = decodeHTMLEntities(entry.country || "");
        const state = decodeHTMLEntities(entry.state || "");
        const city = decodeHTMLEntities(entry.city || "");
        const pincode = entry.pincode || "";

        baseData[`workEntryId${section}`] = entry.id || "";
        baseData[`companyname${section}`] = companyname;
        baseData[`designation${section}`] = designation;
        baseData[`department${section}`] = department;
        baseData[`employmenttype${section}`] = employmenttype;
        baseData[`sector${section}`] = sector;
        baseData[`startdate${section}`] = startdate;
        baseData[`enddate${section}`] = enddate;
        baseData[`currentlyworking${section}`] = currentlyworking;
        baseData[`description${section}`] = description;
        baseData[`address${section}`] = address;
        baseData[`country${section}`] = country;
        baseData[`state${section}`] = state;
        baseData[`city${section}`] = city;
        baseData[`pincode${section}`] = pincode;
  baseData[`workimage${section}`] = entry.image || "";
      });

      const sourceIDCard = Array.isArray(idCardEntries) ? idCardEntries : [];

      sourceIDCard.slice(0, maxIDCardSections).forEach((entry, index) => {
        if (!entry || typeof entry !== "object") return;

        const section = index + 1;
        const idtype = decodeHTMLEntities(entry.idtype || "");
        const idcardname = decodeHTMLEntities(entry.name || entry.idcardname || "");
        const idnumber = entry.idnumber || ""; // Already decrypted by service
        const issuedate = entry.issuedate || ""; // Already decrypted by service
        const expirydate = entry.expirydate || ""; // Already decrypted by service
        const idcarddescription = decodeHTMLEntities(entry.description || entry.idcarddescription || "");

        baseData[`idCardEntryId${section}`] = entry.id || "";
        baseData[`idtype${section}`] = idtype;
        baseData[`idcardname${section}`] = idcardname;
        baseData[`idnumber${section}`] = idnumber;
        baseData[`issuedate${section}`] = issuedate;
        baseData[`expirydate${section}`] = expirydate;
        baseData[`idcarddescription${section}`] = idcarddescription;
      });

      const sourceBank = Array.isArray(bankEntries) ? bankEntries : [];

      sourceBank.slice(0, maxBankSections).forEach((entry, index) => {
        if (!entry || typeof entry !== "object") return;

        const section = index + 1;
        const accountholdername = decodeHTMLEntities(entry.accountholdername || "");
        const accountnumber = entry.accountnumber || ""; // Already decrypted by service
        const ifsc = entry.ifsc || ""; // Already decrypted by service
        const accounttype = decodeHTMLEntities(entry.accounttype || "");
        const bankname = decodeHTMLEntities(entry.bankname || "");
        const branch = decodeHTMLEntities(entry.branch || "");
        const bankcountry = decodeHTMLEntities(entry.bankcountry || "");
        const status = decodeHTMLEntities(entry.status || "Active");
        const bankdescription = decodeHTMLEntities(entry.description || entry.bankdescription || "");

        baseData[`bankEntryId${section}`] = entry.id || "";
        baseData[`accountholdername${section}`] = accountholdername;
        baseData[`accountnumber${section}`] = accountnumber;
        baseData[`ifsc${section}`] = ifsc;
        baseData[`accounttype${section}`] = accounttype;
        baseData[`bankname${section}`] = bankname;
        baseData[`branch${section}`] = branch;
        baseData[`bankcountry${section}`] = bankcountry;
        baseData[`status${section}`] = status;
        baseData[`bankdescription${section}`] = bankdescription;
        baseData[`image${section}`] = entry.image || "";
      });

      const sourceSocialLinks = Array.isArray(socialLinksEntries) ? socialLinksEntries : [];

      sourceSocialLinks.slice(0, maxSocialLinksSections).forEach((entry, index) => {
        if (!entry || typeof entry !== "object") return;

        const section = index + 1;
        const type = decodeHTMLEntities(entry.type || "");
        const link = decodeHTMLEntities(entry.link || "");

        baseData[`socialLinksEntryId${section}`] = entry.id || "";
        baseData[`socialtype${section}`] = type;
        baseData[`sociallink${section}`] = link;
      });

      // Set default educationtype for section 1 if not already set
      if (!baseData.educationtype1) {
        baseData.educationtype1 = "Regular";
      }

      // Add address fields from fetched data
      console.log("🏠 [FORMDATA] Adding address fields - Current:", currentAddressData);
      console.log("🏠 [FORMDATA] Current has data?", currentAddressData && Object.keys(currentAddressData).length > 0);
      console.log("🏠 [FORMDATA] Adding address fields - Permanent:", permanentAddressData);
      console.log("🏠 [FORMDATA] Permanent has data?", permanentAddressData && Object.keys(permanentAddressData).length > 0);
      
      // Current Address - Always initialize fields
      baseData.current_address1 = (currentAddressData && currentAddressData.address) || "";
      baseData.current_street1 = (currentAddressData && currentAddressData.street) || "";
      baseData.current_pincode1 = (currentAddressData && currentAddressData.pincode) || "";
      baseData.current_village1 = (currentAddressData && currentAddressData.village) || "";
      baseData.current_taluk1 = (currentAddressData && currentAddressData.taluk) || "";
      baseData.current_district1 = (currentAddressData && currentAddressData.district) || "";
      baseData.current_state1 = (currentAddressData && currentAddressData.state) || "";
      baseData.current_country1 = (currentAddressData && currentAddressData.country) || "";
      baseData.currentAddressId = (currentAddressData && currentAddressData.id) || "";

      // Permanent Address - Always initialize fields
      baseData.permanent_address1 = (permanentAddressData && permanentAddressData.address) || "";
      baseData.permanent_street1 = (permanentAddressData && permanentAddressData.street) || "";
      baseData.permanent_pincode1 = (permanentAddressData && permanentAddressData.pincode) || "";
      baseData.permanent_village1 = (permanentAddressData && permanentAddressData.village) || "";
      baseData.permanent_taluk1 = (permanentAddressData && permanentAddressData.taluk) || "";
      baseData.permanent_district1 = (permanentAddressData && permanentAddressData.district) || "";
      baseData.permanent_state1 = (permanentAddressData && permanentAddressData.state) || "";
      baseData.permanent_country1 = (permanentAddressData && permanentAddressData.country) || "";
      baseData.permanentAddressId = (permanentAddressData && permanentAddressData.id) || "";
      
      console.log("🏠 [FORMDATA] BaseData after address fields:", {
        current_address1: baseData.current_address1,
        current_pincode1: baseData.current_pincode1,
        current_village1: baseData.current_village1,
        permanent_address1: baseData.permanent_address1,
        permanent_pincode1: baseData.permanent_pincode1,
        permanent_village1: baseData.permanent_village1,
        currentAddressId: baseData.currentAddressId,
        permanentAddressId: baseData.permanentAddressId
      });

      setFormData(baseData);
      setRemovedEducationIds([]);
      setRemovedWorkIds([]);
      setRemovedIDCardIds([]);
      setRemovedBankIds([]);
      setRemovedSocialLinksIds([]);
      setVisibleEducationSections(computeVisibleSections(baseData));
      setVisibleWorkSections(computeVisibleWorkSections(baseData));
      setVisibleIDCardSections(computeVisibleIDCardSections(baseData));
      setVisibleBankSections(computeVisibleBankSections(baseData));
      setVisibleSocialLinksSections(computeVisibleSocialLinksFromHook(baseData));
      setEducationSectionError("");
      setEducationFieldErrors({});
      setWorkSectionError("");
      setWorkFieldErrors({});
      setIDCardSectionError("");
      setIDCardFieldErrors({});
      setBankSectionError("");
      setBankFieldErrors({});
      setSocialLinksSectionError("");
      setSocialLinksFieldErrors({});
    }
  }, [profile, open, countries, states, userPhoto, educationEntries, workEntries, idCardEntries, bankEntries, socialLinksEntries, currentAddressData, permanentAddressData, computeVisibleSections, computeVisibleWorkSections, computeVisibleIDCardSections, computeVisibleBankSections, computeVisibleSocialLinksFromHook]);

  // Separate useEffect to update formData when address data changes
  useEffect(() => {
    if (open && (currentAddressData || permanentAddressData)) {
      console.log("🔄 [ADDRESS UPDATE] Updating formData with address data");
      console.log("🔄 [ADDRESS UPDATE] Current:", currentAddressData);
      console.log("🔄 [ADDRESS UPDATE] Permanent:", permanentAddressData);
      
      setFormData((prevFormData) => {
        const updatedData = { ...prevFormData };
        
        // Update Current Address fields
        if (currentAddressData && Object.keys(currentAddressData).length > 0) {
          updatedData.current_address1 = currentAddressData.address || "";
          updatedData.current_street1 = currentAddressData.street || "";
          updatedData.current_pincode1 = currentAddressData.pincode || "";
          updatedData.current_village1 = currentAddressData.village || "";
          updatedData.current_taluk1 = currentAddressData.taluk || "";
          updatedData.current_district1 = currentAddressData.district || "";
          updatedData.current_state1 = currentAddressData.state || "";
          updatedData.current_country1 = currentAddressData.country || "";
          updatedData.currentAddressId = currentAddressData.id || "";
          
          console.log("✅ [ADDRESS UPDATE] Updated current address fields");
        }
        
        // Update Permanent Address fields
        if (permanentAddressData && Object.keys(permanentAddressData).length > 0) {
          updatedData.permanent_address1 = permanentAddressData.address || "";
          updatedData.permanent_street1 = permanentAddressData.street || "";
          updatedData.permanent_pincode1 = permanentAddressData.pincode || "";
          updatedData.permanent_village1 = permanentAddressData.village || "";
          updatedData.permanent_taluk1 = permanentAddressData.taluk || "";
          updatedData.permanent_district1 = permanentAddressData.district || "";
          updatedData.permanent_state1 = permanentAddressData.state || "";
          updatedData.permanent_country1 = permanentAddressData.country || "";
          updatedData.permanentAddressId = permanentAddressData.id || "";
          
          console.log("✅ [ADDRESS UPDATE] Updated permanent address fields");
        }
        
        console.log("✅ [ADDRESS UPDATE] Final formData:", {
          current_address1: updatedData.current_address1,
          current_pincode1: updatedData.current_pincode1,
          permanent_address1: updatedData.permanent_address1,
          permanent_pincode1: updatedData.permanent_pincode1
        });
        
        return updatedData;
      });
    }
  }, [currentAddressData, permanentAddressData, open]);

  const getSectionRequiredFields = useCallback(
    (section) => [
      `qualification${section}`,
      `board${section}`,
      `educationtype${section}`,
      `instituteschool${section}`,
    ],
    []
  );

  const isSectionPrimaryFieldsFilled = (section) => {
    const requiredFields = getSectionRequiredFields(section);

    return requiredFields.every((fieldName) => {
      const value = formData[fieldName];
      if (typeof value === "string") {
        return value.trim() !== "";
      }
      return value !== undefined && value !== null && value !== "";
    });
  };

  useEffect(() => {
    if (educationSectionError) {
      const currentSection = visibleEducationSections;
      if (isSectionPrimaryFieldsFilled(currentSection)) {
        setEducationSectionError("");
        const fieldsToClear = getSectionRequiredFields(currentSection);
        setEducationFieldErrors((prev) => {
          const next = { ...prev };
          fieldsToClear.forEach((fieldName) => {
            delete next[fieldName];
          });
          return next;
        });
      }
    }
  }, [educationSectionError, visibleEducationSections, formData, getSectionRequiredFields]);

  const handleAddEducationSection = () => {
    if (visibleEducationSections >= maxEducationSections) {
      return;
    }

    const currentSection = visibleEducationSections;
    if (!isSectionPrimaryFieldsFilled(currentSection)) {
      const requiredFields = getSectionRequiredFields(currentSection);
      const missingFields = requiredFields.filter((fieldName) => {
        const value = formData[fieldName];
        if (typeof value === "string") {
          return value.trim() === "";
        }
        return value === undefined || value === null || value === "";
      });

      if (missingFields.length > 0) {
        setEducationFieldErrors((prev) => {
          const next = { ...prev };
          missingFields.forEach((fieldName) => {
            next[fieldName] = "Please fill this field before adding another education entry.";
          });
          return next;
        });
      }

      setEducationSectionError("Please fill the current education details first.");
      return;
    }

    const requiredFields = getSectionRequiredFields(currentSection);
    setEducationFieldErrors((prev) => {
      const next = { ...prev };
      requiredFields.forEach((fieldName) => {
        delete next[fieldName];
      });
      return next;
    });

    const nextSection = visibleEducationSections + 1;
    
    // Set default value for education type in the new section
    setFormData((prev) => ({
      ...prev,
      [`educationtype${nextSection}`]: "Regular",
    }));

    setVisibleEducationSections((prev) => Math.min(prev + 1, maxEducationSections));
    setEducationSectionError("");
  };

  const handleRemoveEducationSection = async (section) => {
    setEducationSectionError("");

    const existingId = formData[`educationEntryId${section}`];
    let deletionPerformed = false;

    if (existingId && sessionId) {
      try {
        await deleteEducationData(sessionId, existingId);
        deletionPerformed = true;
        setEducationEntries((prev) => prev.filter((entry) => entry.id !== existingId));
      } catch (error) {
        console.error("Error deleting education entry:", error);
        setEducationSectionError("Unable to remove education entry. Please try again.");
        return;
      }
    }

    if (existingId) {
      setRemovedEducationIds((prev) => {
        const next = new Set(prev);
        next.add(existingId);
        return Array.from(next);
      });
    }

    setFormData((prev) => {
      const updated = { ...prev };

      for (let current = section; current <= maxEducationSections; current++) {
        const nextSection = current + 1;

        educationFieldKeys.forEach((fieldKey) => {
          const nextKey = `${fieldKey}${nextSection}`;
          const currentKey = `${fieldKey}${current}`;

          if (nextSection <= maxEducationSections) {
            updated[currentKey] = prev[nextKey] ?? "";
          } else {
            updated[currentKey] = "";
          }
        });
      }

      setVisibleEducationSections(computeVisibleSections(updated));
      return updated;
    });

    setEducationFieldErrors({});
  };

  // Work Experience Handlers
  const getWorkSectionRequiredFields = useCallback(
    (section) => [
      `companyname${section}`,
      `designation${section}`,
      `employmenttype${section}`,
      `startdate${section}`,
    ],
    []
  );

  const isWorkSectionPrimaryFieldsFilled = (section) => {
    const requiredFields = getWorkSectionRequiredFields(section);

    return requiredFields.every((fieldName) => {
      const value = formData[fieldName];
      if (typeof value === "string") {
        return value.trim() !== "";
      }
      return value !== undefined && value !== null && value !== "";
    });
  };

  useEffect(() => {
    if (workSectionError) {
      const currentSection = visibleWorkSections;
      if (isWorkSectionPrimaryFieldsFilled(currentSection)) {
        setWorkSectionError("");
        const fieldsToClear = getWorkSectionRequiredFields(currentSection);
        setWorkFieldErrors((prev) => {
          const next = { ...prev };
          fieldsToClear.forEach((fieldName) => {
            delete next[fieldName];
          });
          return next;
        });
      }
    }
  }, [workSectionError, visibleWorkSections, formData, getWorkSectionRequiredFields]);

  const handleAddWorkSection = () => {
    if (visibleWorkSections >= maxWorkSections) {
      return;
    }

    const currentSection = visibleWorkSections;
    if (!isWorkSectionPrimaryFieldsFilled(currentSection)) {
      const requiredFields = getWorkSectionRequiredFields(currentSection);
      const missingFields = requiredFields.filter((fieldName) => {
        const value = formData[fieldName];
        if (typeof value === "string") {
          return value.trim() === "";
        }
        return value === undefined || value === null || value === "";
      });

      if (missingFields.length > 0) {
        setWorkFieldErrors((prev) => {
          const next = { ...prev };
          missingFields.forEach((fieldName) => {
            next[fieldName] = "Please fill this field before adding another work experience entry.";
          });
          return next;
        });
      }

      setWorkSectionError("Please fill the current work experience details first.");
      return;
    }

    const requiredFields = getWorkSectionRequiredFields(currentSection);
    setWorkFieldErrors((prev) => {
      const next = { ...prev };
      requiredFields.forEach((fieldName) => {
        delete next[fieldName];
      });
      return next;
    });

    setVisibleWorkSections((prev) => Math.min(prev + 1, maxWorkSections));
    setWorkSectionError("");
  };

  const handleRemoveWorkSection = async (section) => {
    setWorkSectionError("");

    const existingId = formData[`workEntryId${section}`];
    let deletionPerformed = false;

    if (existingId && sessionId) {
      try {
        await deleteWorkData(sessionId, existingId);
        deletionPerformed = true;
        setWorkEntries((prev) => prev.filter((entry) => entry.id !== existingId));
      } catch (error) {
        console.error("Error deleting work entry:", error);
        setWorkSectionError("Unable to remove work experience entry. Please try again.");
        return;
      }
    }

    if (existingId) {
      setRemovedWorkIds((prev) => {
        const next = new Set(prev);
        next.add(existingId);
        return Array.from(next);
      });
    }

    setFormData((prev) => {
      const updated = { ...prev };

      for (let current = section; current <= maxWorkSections; current++) {
        const nextSection = current + 1;

        workFieldKeys.forEach((fieldKey) => {
          const nextKey = `${fieldKey}${nextSection}`;
          const currentKey = `${fieldKey}${current}`;

          if (nextSection <= maxWorkSections) {
            updated[currentKey] = prev[nextKey] ?? "";
          } else {
            updated[currentKey] = "";
          }
        });
      }

      setVisibleWorkSections(computeVisibleWorkSections(updated));
      return updated;
    });

    setWorkFieldErrors({});
  };

  // ID Card Handlers
  const getIDCardSectionRequiredFields = useCallback(
    (section) => [
      `idtype${section}`,
      `idcardname${section}`,
      `idnumber${section}`,
      `issuedate${section}`,
    ],
    []
  );

  const isIDCardSectionPrimaryFieldsFilled = (section) => {
    const requiredFields = getIDCardSectionRequiredFields(section);

    return requiredFields.every((fieldName) => {
      const value = formData[fieldName];
      if (typeof value === "string") {
        return value.trim() !== "";
      }
      return value !== undefined && value !== null && value !== "";
    });
  };

  useEffect(() => {
    if (idCardSectionError) {
      const currentSection = visibleIDCardSections;
      if (isIDCardSectionPrimaryFieldsFilled(currentSection)) {
        setIDCardSectionError("");
        const fieldsToClear = getIDCardSectionRequiredFields(currentSection);
        setIDCardFieldErrors((prev) => {
          const next = { ...prev };
          fieldsToClear.forEach((fieldName) => {
            delete next[fieldName];
          });
          return next;
        });
      }
    }
  }, [idCardSectionError, visibleIDCardSections, formData, getIDCardSectionRequiredFields]);

  const getBankSectionRequiredFields = useCallback(
    (section) => [
      `accountholdername${section}`,
      `accountnumber${section}`,
      `ifsc${section}`,
      `bankname${section}`,
      `branch${section}`,
    ],
    []
  );

  const isBankSectionPrimaryFieldsFilled = (section) => {
    const requiredFields = getBankSectionRequiredFields(section);

    return requiredFields.every((fieldName) => {
      const value = formData[fieldName];
      if (typeof value === "string") {
        return value.trim() !== "";
      }
      return value !== undefined && value !== null && value !== "";
    });
  };

  useEffect(() => {
    if (bankSectionError) {
      const currentSection = visibleBankSections;
      if (isBankSectionPrimaryFieldsFilled(currentSection)) {
        setBankSectionError("");
        const fieldsToClear = getBankSectionRequiredFields(currentSection);
        setBankFieldErrors((prev) => {
          const next = { ...prev };
          fieldsToClear.forEach((fieldName) => {
            delete next[fieldName];
          });
          return next;
        });
      }
    }
  }, [bankSectionError, visibleBankSections, formData, getBankSectionRequiredFields]);

  const handleAddIDCardSection = () => {
    if (visibleIDCardSections >= maxIDCardSections) {
      return;
    }

    const currentSection = visibleIDCardSections;
    if (!isIDCardSectionPrimaryFieldsFilled(currentSection)) {
      const requiredFields = getIDCardSectionRequiredFields(currentSection);
      const missingFields = requiredFields.filter((fieldName) => {
        const value = formData[fieldName];
        if (typeof value === "string") {
          return value.trim() === "";
        }
        return value === undefined || value === null || value === "";
      });

      if (missingFields.length > 0) {
        setIDCardFieldErrors((prev) => {
          const next = { ...prev };
          missingFields.forEach((fieldName) => {
            next[fieldName] = "Please fill this field before adding another ID card entry.";
          });
          return next;
        });
      }

      setIDCardSectionError("Please fill the current ID card details first.");
      return;
    }

    const requiredFields = getIDCardSectionRequiredFields(currentSection);
    setIDCardFieldErrors((prev) => {
      const next = { ...prev };
      requiredFields.forEach((fieldName) => {
        delete next[fieldName];
      });
      return next;
    });

    setVisibleIDCardSections((prev) => Math.min(prev + 1, maxIDCardSections));
    setIDCardSectionError("");
  };

  const handleRemoveIDCardSection = async (section) => {
    setIDCardSectionError("");

    const existingId = formData[`idCardEntryId${section}`];
    let deletionPerformed = false;

    if (existingId && sessionId) {
      try {
        await deleteIDCardData(sessionId, existingId);
        deletionPerformed = true;
        setIDCardEntries((prev) => prev.filter((entry) => entry.id !== existingId));
      } catch (error) {
        console.error("Error deleting ID card entry:", error);
        setIDCardSectionError("Unable to remove ID card entry. Please try again.");
        return;
      }
    }

    if (existingId) {
      setRemovedIDCardIds((prev) => {
        const next = new Set(prev);
        next.add(existingId);
        return Array.from(next);
      });
    }

    setFormData((prev) => {
      const updated = { ...prev };

      for (let current = section; current <= maxIDCardSections; current++) {
        const nextSection = current + 1;

        idCardFieldKeys.forEach((fieldKey) => {
          const nextKey = `${fieldKey}${nextSection}`;
          const currentKey = `${fieldKey}${current}`;

          if (nextSection <= maxIDCardSections) {
            updated[currentKey] = prev[nextKey] ?? "";
          } else {
            updated[currentKey] = "";
          }
        });
      }

      setVisibleIDCardSections(computeVisibleIDCardSections(updated));
      return updated;
    });

    setIDCardFieldErrors({});
  };

  const handleAddBankSection = () => {
    if (visibleBankSections >= maxBankSections) {
      return;
    }

    const currentSection = visibleBankSections;
    if (!isBankSectionPrimaryFieldsFilled(currentSection)) {
      const requiredFields = getBankSectionRequiredFields(currentSection);
      const missingFields = requiredFields.filter((fieldName) => {
        const value = formData[fieldName];
        if (typeof value === "string") {
          return value.trim() === "";
        }
        return value === undefined || value === null || value === "";
      });

      if (missingFields.length > 0) {
        setBankFieldErrors((prev) => {
          const next = { ...prev };
          missingFields.forEach((fieldName) => {
            next[fieldName] = "Please fill this field before adding another bank account.";
          });
          return next;
        });
      }

      setBankSectionError("Please fill the current bank details first.");
      return;
    }

    const requiredFields = getBankSectionRequiredFields(currentSection);
    setBankFieldErrors((prev) => {
      const next = { ...prev };
      requiredFields.forEach((fieldName) => {
        delete next[fieldName];
      });
      return next;
    });

    setVisibleBankSections((prev) => Math.min(prev + 1, maxBankSections));
    setBankSectionError("");
  };

  const handleRemoveBankSection = async (section) => {
    setBankSectionError("");

    const existingId = formData[`bankEntryId${section}`];
    let deletionPerformed = false;

    if (existingId && sessionId) {
      try {
        await deleteBankDetailsData(sessionId, existingId);
        deletionPerformed = true;
        setBankEntries((prev) => prev.filter((entry) => entry.id !== existingId));
      } catch (error) {
        console.error("Error deleting bank details entry:", error);
        setBankSectionError("Unable to remove bank details entry. Please try again.");
        return;
      }
    }

    if (existingId) {
      setRemovedBankIds((prev) => {
        const next = new Set(prev);
        next.add(existingId);
        return Array.from(next);
      });
    }

    setFormData((prev) => {
      const updated = { ...prev };

      for (let current = section; current <= maxBankSections; current++) {
        const nextSection = current + 1;

        bankFieldKeys.forEach((fieldKey) => {
          const nextKey = `${fieldKey}${nextSection}`;
          const currentKey = `${fieldKey}${current}`;

          if (nextSection <= maxBankSections) {
            updated[currentKey] = prev[nextKey] ?? "";
          } else {
            updated[currentKey] = "";
          }
        });
      }

      setVisibleBankSections(computeVisibleBankSections(updated));
      return updated;
    });

    setBankFieldErrors({});
  };

  const handlePhotoUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      if (file.size > 15 * 1024 * 1024) {
        alert("File size must be less than 15MB");
        return;
      }
      const reader = new FileReader();
      reader.onloadend = () => {
        setProfilePhoto(reader.result);
        setPhotoRemoved(false);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleRemovePhoto = () => {
    setProfilePhoto(null);
    setPhotoRemoved(true);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleOpenCamera = () => {
    setCameraDialogOpen(true);
  };

  const handleCloseCamera = () => {
    setCameraDialogOpen(false);
  };

  const handleCapturePhoto = () => {
    if (webcamRef.current) {
      const imageSrc = webcamRef.current.getScreenshot();
      setProfilePhoto(imageSrc);
      setPhotoRemoved(false);
      setCameraDialogOpen(false);
    }
  };

  const handleMobileChange = (value) => {
    setFormData((prev) => ({
      ...prev,
      mobile: value || "",
    }));
    if (value && value.length > 5) {
      setMobileError(false);
    }
  };

  const handleMobileCountryChange = (country) => {
    setPhoneCountry(country);
  };

  const handleFieldBlur = (fieldName) => {
    setTouchedFields((prev) => ({
      ...prev,
      [fieldName]: true,
    }));
  };

  const handleDeathLocationChange = (location) => {
    const stateObj = states.find(
      (s) => s.name === location.state || s.isoCode === location.state
    ) || null;
    const countryObj = countries.find(
      (c) => c.name === location.country || c.isoCode === location.country
    ) || null;
    handleChange({ target: { name: "deathcity_c", value: location.city || "" } });
    handleChange({ target: { name: "deathstate_c", value: stateObj } });
    handleChange({ target: { name: "deathcountry_c", value: countryObj } });
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === "disability_c" && String(value || "").trim()) {
      setDisabilityValidationError("");
    }
    if (name === "disabilitytype_c" && value !== "Yes") {
      setDisabilityValidationError("");
    }
    setFormData((prev) => {
      const updated = {
        ...prev,
        [name]: value,
      };

      setEducationFieldErrors((prevErrors) => {
        if (!prevErrors[name]) {
          return prevErrors;
        }
        const nextErrors = { ...prevErrors };
        delete nextErrors[name];
        return nextErrors;
      });

      const qualificationMatch = name.match(/^qualification(\d+)$/);
      if (qualificationMatch) {
        const section = qualificationMatch[1];
        updated[`education${section}`] = value;
      }

      const educationMatch = name.match(/^education(\d+)$/);
      if (educationMatch) {
        const section = educationMatch[1];
        updated[`qualification${section}`] = value;
      }

      const instituteSchoolMatch = name.match(/^instituteschool(\d+)$/);
      if (instituteSchoolMatch) {
        const section = instituteSchoolMatch[1];
        updated[`institute${section}`] = value;
      }

      const instituteMatch = name.match(/^institute(\d+)$/);
      if (instituteMatch) {
        const section = instituteMatch[1];
        updated[`instituteschool${section}`] = value;
      }

      // Sync permanent address when "Same as Current" is checked
      if (sameAsCurrent && name.startsWith('current_')) {
        const permanentFieldName = name.replace('current_', 'permanent_');
        updated[permanentFieldName] = value;
      }

      return updated;
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Block save if caste/religion contain non-listed values
    if (casteInvalid) return;
    if (religionInvalid) return;

    const disabilityStatus = formData.disabilitytype_c || formData.disabilitytype || "";
    const disabilityValue = (formData.disability_c || "").trim();
    if (disabilityStatus === "Yes" && !disabilityValue) {
      setDisabilityValidationError("Disability is required when Disability Status is Yes.");
      return;
    }
    setDisabilityValidationError("");

    try {
      // Fetch all existing addresses to handle duplicates
      const existingAddresses = await fetchAddressDetailsByParent(sessionId, userId);
      console.log("🏠 [SUBMIT] Existing addresses:", existingAddresses);
      
      // Get all Current address IDs (to delete duplicates, keep the one we're updating)
      const currentAddressIds = existingAddresses
        .filter(addr => addr.addresstype_c === "Current")
        .map(addr => addr.id);
      
      const permanentAddressIds = existingAddresses
        .filter(addr => addr.addresstype_c === "Permanent")
        .map(addr => addr.id);
      
      console.log("🏠 [SUBMIT] Found", currentAddressIds.length, "current addresses");
      console.log("🏠 [SUBMIT] Found", permanentAddressIds.length, "permanent addresses");
      
      // Save Current Address
      const currentAddressPayload = {
        parentid: userId,
        addresstype_c: "Current",
        address: formData.current_address1 || "",
        street: formData.current_street1 || "",
        pincode: formData.current_pincode1 || "",
        village: formData.current_village1 || "",
        taluk: formData.current_taluk1 || "",
        district: formData.current_district1 || "",
        state: formData.current_state1 || "",
        country: formData.current_country1 || "",
      };

      console.log("🏠 [SUBMIT] Current Address Payload:", currentAddressPayload);
      console.log("🏠 [SUBMIT] Current Address ID:", formData.currentAddressId);

      if (formData.currentAddressId) {
        // Update existing
        console.log("🏠 [SUBMIT] Updating existing current address");
        await editAddressData(sessionId, formData.currentAddressId, currentAddressPayload);
        
        // Delete duplicates (other than the one we just updated)
        const duplicatesToDelete = currentAddressIds.filter(id => id !== formData.currentAddressId);
        for (const dupId of duplicatesToDelete) {
          console.log("🏠 [SUBMIT] Deleting duplicate current address:", dupId);
          await deleteAddressData(sessionId, dupId);
        }
      } else if (currentAddressPayload.address || currentAddressPayload.pincode) {
        // Add new only if there's data
        console.log("🏠 [SUBMIT] Adding new current address");
        await addAddressData(sessionId, currentAddressPayload);
        
        // Delete all old entries
        for (const oldId of currentAddressIds) {
          console.log("🏠 [SUBMIT] Deleting old current address:", oldId);
          await deleteAddressData(sessionId, oldId);
        }
      } else {
        console.log("🏠 [SUBMIT] No current address data to save");
      }

      // Save Permanent Address
      const permanentAddressPayload = {
        parentid: userId,
        addresstype_c: "Permanent",
        address: formData.permanent_address1 || "",
        street: formData.permanent_street1 || "",
        pincode: formData.permanent_pincode1 || "",
        village: formData.permanent_village1 || "",
        taluk: formData.permanent_taluk1 || "",
        district: formData.permanent_district1 || "",
        state: formData.permanent_state1 || "",
        country: formData.permanent_country1 || "",
      };

      console.log("🏠 [SUBMIT] Permanent Address Payload:", permanentAddressPayload);
      console.log("🏠 [SUBMIT] Permanent Address ID:", formData.permanentAddressId);

      if (formData.permanentAddressId) {
        // Update existing
        console.log("🏠 [SUBMIT] Updating existing permanent address");
        await editAddressData(sessionId, formData.permanentAddressId, permanentAddressPayload);
        
        // Delete duplicates (other than the one we just updated)
        const duplicatesToDelete = permanentAddressIds.filter(id => id !== formData.permanentAddressId);
        for (const dupId of duplicatesToDelete) {
          console.log("🏠 [SUBMIT] Deleting duplicate permanent address:", dupId);
          await deleteAddressData(sessionId, dupId);
        }
      } else if (permanentAddressPayload.address || permanentAddressPayload.pincode) {
        // Add new only if there's data
        console.log("🏠 [SUBMIT] Adding new permanent address");
        await addAddressData(sessionId, permanentAddressPayload);
        
        // Delete all old entries
        for (const oldId of permanentAddressIds) {
          console.log("🏠 [SUBMIT] Deleting old permanent address:", oldId);
          await deleteAddressData(sessionId, oldId);
        }
      } else {
        console.log("🏠 [SUBMIT] No permanent address data to save");
      }
    } catch (error) {
      console.error("❌ [SUBMIT] Error saving address data:", error);
      // Continue with form submission even if address save fails
    }

    const submitData = {
      ...formData,
      photo: profilePhoto,
      photoRemoved: photoRemoved && hadPhotoBeforeRemoval,
      removedEducationIds,
      removedWorkIds,
      removedIDCardIds,
      removedBankIds,
    };

    onSubmit(submitData);
  };

  const textInputStyle = {
    "& .MuiInputBase-root": {
      height: "56px",
      fontSize: "16px",
    },
    "& .MuiOutlinedInput-input": {
      padding: "0 14px",
    },
  };

  const shouldShowRemovePhotoButton = profilePhoto && !photoRemoved;

  // "Add master data" integration
  const [addDialog, setAddDialog] = useState({ open: false, category: null, initialValue: "" });

  const listIncludes = (coll, val) => {
    const v = (val || "").trim().toLowerCase();
    if (!v) return false;
    return (coll || []).some((i) => ((i?.name ?? i) || "").toLowerCase() === v);
  };

  const casteInvalid = useMemo(() => {
    const val = (formData.caste || "").trim();
    if (!val) return false; // allow empty
    return !listIncludes(castes, val);
  }, [formData.caste, castes]);

  const religionInvalid = useMemo(() => {
    const val = (formData.religion || "").trim();
    if (!val) return false; // allow empty
    return !listIncludes(religions, val);
  }, [formData.religion, religions]);

  const shouldOfferAddCaste = useMemo(() => {
    const v = (formData.caste || "").trim();
    return !!v && casteInvalid;
  }, [formData.caste, casteInvalid]);

  const shouldOfferAddReligion = useMemo(() => {
    const v = (formData.religion || "").trim();
    return !!v && religionInvalid;
  }, [formData.religion, religionInvalid]);

  const openAddMasterDialog = (category, value) => {
    setAddDialog({ open: true, category, initialValue: value || "" });
  };
  const closeAddMasterDialog = () => setAddDialog((p) => ({ ...p, open: false }));

  const sortByName = (arr = []) =>
    [...arr].sort((a, b) => (a?.name || "").localeCompare(b?.name || ""));

  const handleAddMasterSuccess = (created) => {
    if (!created?.name || !created?.category_c) {
      closeAddMasterDialog();
      return;
    }
    if (created.category_c === "Caste") {
      // Update local list and select the newly added value
      // castes is a state variable in this component
      // Append and sort
      setCastes((prev) => sortByName([...(prev || []), created]));
      handleChange({ target: { name: "caste", value: created.name } });
    } else if (created.category_c === "Religion") {
      setReligions((prev) => sortByName([...(prev || []), created]));
      handleChange({ target: { name: "religion", value: created.name } });
    }
    closeAddMasterDialog();
  };

  return (
    <>
      <style>{`
        .PhoneInputInput {
          height: 56px !important;
          border: 1px solid #ccc !important;
          border-radius: 4px !important;
          padding: 0 14px !important;
        }
        .PhoneInputInput:focus {
          border-color: #1976d2 !important;
          box-shadow: 0 0 0 1px #1976d2 !important;
          outline: none;
        }
        .phone-input-error .PhoneInputInput {
          border-color: #ef4444 !important;
          box-shadow: 0 0 0 1px rgba(239, 68, 68, 0.15) !important;
        }
        .phone-input-wrapper {
          display: flex;
          gap: 8px;
        }
        .PhoneInputCountry {
          flex: 1 1 0;
          min-width: 0;
        }
        .PhoneInputCountrySelect {
          font-size: 14px !important;
        }
        .PhoneInputInput {
          flex: 1 1 0;
          min-width: 0;
        }
      `}</style>

      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          style: {
            maxHeight: "90vh",
          },
        }}
      >
        <DialogTitle className="flex justify-between items-center" sx={{ p: 3 }}>
          <Typography variant="h5" component="span" fontWeight="bold">
            ✏️ Edit Profile
          </Typography>
          <IconButton onClick={onClose} disabled={isSubmitting}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ p: 4 }}>
          {error && (
            <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit}>
            <Grid container spacing={3} sx={{ mt: 1 }}>
              {/* Photo Upload Section */}
              <Grid item xs={12}>
                <Box className="flex flex-col md:flex-row gap-6 items-center p-4 bg-gray-50 rounded-lg border-2 border-blue-200">
                  <Box className="flex-shrink-0">
                    <Avatar
                      src={profilePhoto || (formData.gender === "Female" ? Female : Male)}
                      alt="Profile"
                      sx={{
                        width: 120,
                        height: 120,
                        border: "3px solid #e0e0e0",
                      }}
                    />
                  </Box>
                  <Box className="flex-grow">
                    <Typography variant="h6" gutterBottom>
                      Profile Photo
                    </Typography>
                    <Box className="flex flex-wrap gap-2 mb-2">
                      <input
                        type="file"
                        accept="image/*"
                        onChange={handlePhotoUpload}
                        ref={fileInputRef}
                        style={{ display: "none" }}
                        id="profile-photo-upload"
                      />
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={() => fileInputRef.current?.click()}
                        sx={{
                          color: "#1976d2",
                          borderColor: "#1976d2",
                          backgroundColor: "#fff",
                          fontWeight: "medium",
                          "&:hover": {
                            backgroundColor: "#e3f2fd",
                            borderColor: "#1565c0",
                            color: "#1565c0",
                          },
                        }}
                      >
                        Choose Photo
                      </Button>

                      <Button
                        variant="outlined"
                        size="small"
                        startIcon={<CameraAltIcon />}
                        onClick={handleOpenCamera}
                        sx={{
                          color: "#1976d2",
                          borderColor: "#1976d2",
                          backgroundColor: "#fff",
                          fontWeight: "medium",
                          "&:hover": {
                            backgroundColor: "#e3f2fd",
                            borderColor: "#1565c0",
                            color: "#1565c0",
                          },
                        }}
                      >
                        Camera
                      </Button>

                      {shouldShowRemovePhotoButton && (
                        <Button
                          variant="outlined"
                          color="error"
                          size="small"
                          onClick={handleRemovePhoto}
                          sx={{
                            color: "#d32f2f",
                            borderColor: "#d32f2f",
                            backgroundColor: "#fff",
                            fontWeight: "medium",
                            "&:hover": {
                              backgroundColor: "#ffebee",
                              borderColor: "#c62828",
                              color: "#c62828",
                            },
                          }}
                        >
                          Remove Photo
                        </Button>
                      )}
                    </Box>
                    <Typography variant="caption" color="text.secondary">
                      Max size: 15MB. Supported: JPG, PNG, GIF
                      {photoRemoved && hadPhotoBeforeRemoval && (
                        <span style={{ color: "#f44336", fontWeight: "bold" }}>
                          <br />
                          ⚠️ Photo will be removed when you save
                        </span>
                      )}
                    </Typography>
                  </Box>
                </Box>
              </Grid>

              {/* Name Fields */}
              <Grid item xs={12} md={6}>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  First Name <span className="text-red-500">*</span>
                </label>
                <TextField
                  fullWidth
                  name="firstName"
                  value={formData.firstName || ""}
                  onChange={handleChange}
                  variant="outlined"
                  placeholder="Enter first name"
                  required
                  sx={textInputStyle}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <label className="block text-sm font-medium text-gray-700 mb-1">Last Name</label>
                <TextField
                  fullWidth
                  name="lastName"
                  value={formData.lastName || ""}
                  onChange={handleChange}
                  variant="outlined"
                  placeholder="Enter last name"
                  sx={textInputStyle}
                />
              </Grid>

              {/* Mobile Field */}
              <Grid item xs={12} md={6}>
                <FormControl fullWidth error={mobileError} sx={{ mt: 0 }}>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Mobile <span className="text-red-500">*</span>
                  </label>
                  <PhoneInput
                    international
                    withCountryCallingCode
                    value={formData.mobile}
                    onChange={handleMobileChange}
                    onCountryChange={handleMobileCountryChange}
                    country={phoneCountry}
                    onBlur={() => handleFieldBlur("mobile")}
                    labels={phoneLabels}
                    countrySelectComponent={PhoneCountryAutocomplete}
                    countrySelectProps={{
                      textFieldProps: {
                        size: "medium",
                        label: "Country",
                        placeholder: "Search country",
                      },
                      sx: {
                        width: "50%",
                        minWidth: "140px",
                        mr: 1,
                        "& .MuiInputBase-root": {
                          height: "56px",
                          fontSize: "14px",
                        },
                      },
                    }}
                    className={`phone-input-wrapper w-full ${
                      mobileError ? "phone-input-error" : ""
                    }`}
                    placeholder="Enter mobile number"
                    disabled
                    required
                  />
                  <FormHelperText sx={{ minHeight: "20px" }}>
                    You can change your registered mobile number
                  </FormHelperText>
                </FormControl>
              </Grid>

              {/* Email Field */}
              <Grid item xs={12} md={6}>
                <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                <TextField
                  fullWidth
                  name="email"
                  type="email"
                  value={formData.email || ""}
                  onChange={handleChange}
                  variant="outlined"
                  placeholder="Enter email address"
                  disabled
                  sx={textInputStyle}
                  helperText="You can change your registered email"
                />
              </Grid>

              {/* Aadhaar Field */}
              <Grid item xs={12} md={6}>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Aadhaar Number
                </label>
                <TextField
                  fullWidth
                  name="aadhaar"
                  value={formData.aadhaar || ""}
                  onChange={handleChange}
                  onBlur={() => handleFieldBlur("aadhaar")}
                  variant="outlined"
                  placeholder="Enter 12-digit Aadhaar number"
                  inputProps={{
                    maxLength: 12,
                    pattern: "[0-9]*",
                    inputMode: "numeric",
                  }}
                  sx={textInputStyle}
                  helperText={
                    touchedFields.aadhaar && formData.aadhaar && formData.aadhaar.length !== 12
                      ? "Aadhaar number must be exactly 12 digits"
                      : " "
                  }
                  FormHelperTextProps={{
                    sx: {
                      visibility:
                        touchedFields.aadhaar && formData.aadhaar && formData.aadhaar.length !== 12
                          ? "visible"
                          : "hidden",
                    },
                  }}
                  error={
                    touchedFields.aadhaar && formData.aadhaar && formData.aadhaar.length !== 12
                  }
                />
              </Grid>

              {/* Caste Field */}
              <Grid item xs={12} md={6}>
                <label className="block text-sm font-medium text-gray-700 mb-1">Caste</label>
                <Autocomplete
                  fullWidth
                  options={castes.map((item) => item.name)}
                  getOptionLabel={(option) => option}
                  value={formData.caste || null}
                  onChange={(e, value) => handleChange({ target: { name: "caste", value } })}
                  onInputChange={(e, value) =>
                    handleChange({ target: { name: "caste", value: value || "" } })
                  }
                  freeSolo
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      variant="outlined"
                      placeholder="Select or enter caste"
                      error={casteInvalid}
                      helperText={
                        casteInvalid
                          ? 'Value not in list. Click "Add caste" below to add it or clear the field.'
                          : " "
                      }
                      sx={textInputStyle}
                    />
                  )}
                />
                {shouldOfferAddCaste && (
                  <Box sx={{ mt: 1 }}>
                    <Button
                      size="small"
                      variant="text"
                      onClick={() => openAddMasterDialog("Caste", formData.caste)}
                    >
                      Add caste "{formData.caste}"
                    </Button>
                  </Box>
                )}
              </Grid>

              {/* Religion Field */}
              <Grid item xs={12} md={6}>
                <label className="block text-sm font-medium text-gray-700 mb-1">Religion</label>
                <Autocomplete
                  fullWidth
                  options={religions.map((item) => item.name)}
                  getOptionLabel={(option) => option}
                  value={formData.religion || null}
                  onChange={(e, value) => handleChange({ target: { name: "religion", value } })}
                  onInputChange={(e, value) =>
                    handleChange({ target: { name: "religion", value: value || "" } })
                  }
                  freeSolo
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      variant="outlined"
                      placeholder="Select or enter religion"
                      error={religionInvalid}
                      helperText={
                        religionInvalid
                          ? 'Value not in list. Click "Add religion" below to add it or clear the field.'
                          : " "
                      }
                      sx={textInputStyle}
                    />
                  )}
                />
                {shouldOfferAddReligion && (
                  <Box sx={{ mt: 1 }}>
                    <Button
                      size="small"
                      variant="text"
                      onClick={() => openAddMasterDialog("Religion", formData.religion)}
                    >
                      Add religion "{formData.religion}"
                    </Button>
                  </Box>
                )}
              </Grid>

              {/* Personal Details Section */}
              <Grid item xs={12}>
                <div className="mt-6 mb-4">
                  <h3 className="text-lg font-semibold text-gray-800 mb-4 border-b-2 border-blue-500 pb-2">
                    🧍‍♂️ 1. Personal Details
                  </h3>
                  <Grid container spacing={3}>
                    <Grid item xs={12} md={6}>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Date of Birth
                      </label>
                      <TextField
                        fullWidth
                        type="date"
                        name="dob"
                        value={formData.dob || ""}
                        onChange={handleChange}
                        variant="outlined"
                        InputLabelProps={{ shrink: true }}
                        sx={textInputStyle}
                      />
                    </Grid>

                    <Grid item xs={12} md={6}>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Age</label>
                      <TextField
                        fullWidth
                        type="number"
                        name="age"
                        value={formData.age || ""}
                        onChange={handleChange}
                        variant="outlined"
                        placeholder="Enter age"
                        sx={textInputStyle}
                      />
                    </Grid>

                    <Grid item xs={12} md={6}>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Gender</label>
                      <Autocomplete
                        options={["Male", "Female", "Other", "Prefer not to disclose"]}
                        value={formData.gender || null}
                        onChange={(e, value) => handleChange({ target: { name: "gender", value } })}
                        renderInput={(params) => (
                          <TextField {...params} placeholder="Select gender" sx={textInputStyle} />
                        )}
                      />
                    </Grid>

                    <Grid item xs={12} md={6}>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Blood Group
                      </label>
                      <Autocomplete
                        options={[
                          "A+",
                          "A-",
                          "B+",
                          "B-",
                          "O+",
                          "O-",
                          "AB+",
                          "AB-",
                          "Prefer not to disclose",
                        ]}
                        value={formData.bloodgroup || null}
                        onChange={(e, value) =>
                          handleChange({ target: { name: "bloodgroup", value } })
                        }
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            placeholder="Select or enter blood group"
                            sx={textInputStyle}
                          />
                        )}
                      />
                    </Grid>

                    <Grid item xs={12} md={6}>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Birth Place
                      </label>
                      <TextField
                        fullWidth
                        name="birthplace"
                        value={formData.birthplace || ""}
                        onChange={handleChange}
                        variant="outlined"
                        placeholder="Enter birth place"
                        sx={textInputStyle}
                      />
                    </Grid>

                    <Grid item xs={12} md={6}>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Disability Status
                      </label>
                      <Autocomplete
                        options={["Yes", "No", "Prefer not to disclose"]}
                        value={formData.disabilitytype_c || formData.disabilitytype || null}
                        onChange={(e, value) => {
                          const status = value || "";
                          if (status !== "Yes") {
                            setDisabilityValidationError("");
                          }
                          setFormData((prev) => ({
                            ...prev,
                            disabilitytype_c: status,
                            disabilitytype: status,
                            disability_c: status === "Yes" ? prev.disability_c : "",
                          }));
                        }}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            placeholder="Select disability status"
                            sx={textInputStyle}
                          />
                        )}
                      />
                    </Grid>

                    {formData.disabilitytype_c === "Yes" && (
                      <Grid item xs={12} md={6}>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Disability <span className="text-red-500">*</span>
                        </label>
                        <TextField
                          fullWidth
                          required
                          name="disability_c"
                          value={formData.disability_c || ""}
                          onChange={handleChange}
                          variant="outlined"
                          placeholder="Enter disability"
                          error={!!disabilityValidationError}
                          helperText={disabilityValidationError || ""}
                          sx={textInputStyle}
                        />
                      </Grid>
                    )}
                    <Grid item xs={12} md={6}>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Alive Status
                      </label>
                      <Autocomplete
                        options={[
                          "Alive",
                          "Deceased / Passed away / No longer living / Rest in peace",
                        ]}
                        value={formData.alivestatus || "Alive"}
                        onChange={(e, value) =>
                          handleChange({ target: { name: "alivestatus", value } })
                        }
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            placeholder="Select alive status"
                            sx={textInputStyle}
                          />
                        )}
                      />
                    </Grid>

                    {/* Show death fields only if the selected option indicates death */}
               {formData.alivestatus?.includes("Deceased") && (
                      <>
                        <Grid item xs={12} md={6}>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Date of Death
                          </label>
                          <TextField
                            fullWidth
                            type="date"
                            name="dateofdeath_c"
                            value={formData.dateofdeath_c || ""}
                            onChange={handleChange}
                            variant="outlined"
                            InputLabelProps={{ shrink: true }}
                            sx={textInputStyle}
                          />
                        </Grid>

                        <Grid item xs={12} md={6}>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Death Place
                          </label>
                          <TextField
                            fullWidth
                            name="deathplace_c"
                            value={formData.deathplace_c || ""}
                            onChange={handleChange}
                            variant="outlined"
                            placeholder="Enter death place"
                            sx={textInputStyle}
                          />
                        </Grid>

                        <Grid item xs={12} md={6}>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Death Reason
                          </label>
                          <TextField
                            fullWidth
                            name="deathreason_c"
                            value={formData.deathreason_c || ""}
                            onChange={handleChange}
                            variant="outlined"
                            placeholder="Enter death reason"
                            sx={textInputStyle}
                          />
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Death Address
                          </label>
                          <TextField
                            fullWidth
                            name="deathaddress_c"
                            value={formData.deathaddress_c || ""}
                            onChange={handleChange}
                            variant="outlined"
                            placeholder="Enter death address"
                            sx={textInputStyle}
                          />
                        </Grid>
                        
                        {/* Death Pincode with Autofill - Must be before Death Area */}
                        <Grid item xs={12} md={6}>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Death Pincode {deathPincodeLoading && "🔄"}
                          </label>
                          <TextField
                            fullWidth
                            name="deathpincode_c"
                            value={formData.deathpincode_c || ""}
                            onChange={(e) => {
                              const value = e.target.value;
                              handleChange(e);
                              // Trigger autofill when 6-digit pincode is entered
                              if (value.length === 6 && /^\d{6}$/.test(value)) {
                                handleDeathPincodeInput(value);
                              }
                            }}
                            variant="outlined"
                            placeholder="Enter 6-digit pincode"
                            inputProps={{
                              maxLength: 6,
                              pattern: "[0-9]*",
                              inputMode: "numeric"
                            }}
                            helperText={
                              deathPincodeLoading
                                ? "🔄 Fetching location details..."
                                : deathPincodeError
                                ? `⚠️ ${deathPincodeError}`
                                : "Enter 6-digit pincode to auto-fill location"
                            }
                            error={Boolean(deathPincodeError)}
                            sx={textInputStyle}
                          />
                        </Grid>

                        {/* Death Area - Dropdown populated from pincode */}
                        <Grid item xs={12} md={6}>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Death Area{" "}
                            {deathAreaOptions.length > 0 && (
                              <span className="text-xs text-blue-600">
                                ({deathAreaOptions.length} options from pincode)
                              </span>
                            )}
                          </label>
                          <Autocomplete
                            fullWidth
                            options={deathAreaOptions}
                            getOptionLabel={(option) => option || ""}
                            value={formData.deatharea_c || null}
                            onChange={(e, value) =>
                              handleChange({ target: { name: "deatharea_c", value } })
                            }
                            onInputChange={(e, value) => {
                              if (e?.type === "change") {
                                handleChange({ target: { name: "deatharea_c", value } });
                              }
                            }}
                            freeSolo
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                variant="outlined"
                                placeholder={
                                  deathAreaOptions.length > 0
                                    ? "Select from dropdown or type area"
                                    : "Enter death pincode to see options"
                                }
                                helperText={
                                  deathAreaOptions.length > 0
                                    ? `${deathAreaOptions.length} areas available from pincode`
                                    : "Area dropdown will populate after entering valid pincode"
                                }
                                sx={textInputStyle}
                              />
                            )}
                          />
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Death City
                          </label>
                          <Autocomplete
                            fullWidth
                            options={CITIES}
                            getOptionLabel={(option) => option}
                            value={formData.deathcity_c || null}
                            onChange={(e, value) => handleChange({ target: { name: "deathcity_c", value } })}
                            onInputChange={(e, value) => {
                              if (e?.type === "change") {
                                handleChange({ target: { name: "deathcity_c", value } });
                              }
                            }}
                            freeSolo
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                variant="outlined"
                                placeholder="Select or enter death city"
                                sx={textInputStyle}
                              />
                            )}
                          />
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Death State
                          </label>
                          <Autocomplete
                            fullWidth
                            options={formData.deathcountry_c ? states.filter((state) => state.countryCode === formData.deathcountry_c.isoCode) : states}
                            getOptionLabel={(option) => (typeof option === "string" ? option : option.name)}
                            value={formData.deathstate_c || null}
                            onChange={(e, value) => handleChange({ target: { name: "deathstate_c", value } })}
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                variant="outlined"
                                placeholder="Select death state"
                                sx={textInputStyle}
                              />
                            )}
                          />
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Death Country
                          </label>
                          <Autocomplete
                            fullWidth
                            options={countries}
                            getOptionLabel={(option) => (typeof option === "string" ? option : option.name)}
                            value={formData.deathcountry_c || null}
                            onChange={(e, value) => handleChange({ target: { name: "deathcountry_c", value } })}
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                variant="outlined"
                                placeholder="Select death country"
                                sx={textInputStyle}
                              />
                            )}
                          />
                        </Grid>
                      </>
                    )}

                    <Grid item xs={12} md={6}>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Marital Status
                      </label>
                      <Autocomplete
                        options={[
                          "Single / Unmarried / Spinster",
                          "Married",
                          "Divorced",
                          "Widowed",
                          "Prefer not to disclose",
                        ]}
                        value={formData.maritalstatus || null}
                        onChange={(e, value) =>
                          handleChange({ target: { name: "maritalstatus", value } })
                        }
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            placeholder="Select marital status"
                            sx={textInputStyle}
                          />
                        )}
                      />
                    </Grid>

                    {(formData.maritalstatus === "Married" ||
                      formData.maritalstatus === "Divorced" ||
                      formData.maritalstatus === "Widowed") && (
                      <>
                        <Grid item xs={12} md={6}>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Marriage Date
                          </label>
                          <TextField
                            fullWidth
                            type="date"
                            name="marriagedate"
                            value={formData.marriagedate || ""}
                            onChange={handleChange}
                            variant="outlined"
                            InputLabelProps={{ shrink: true }}
                            sx={textInputStyle}
                          />
                        </Grid>

                        <Grid item xs={12} md={6}>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Marriage Place
                          </label>
                          <TextField
                            fullWidth
                            name="marriageplace"
                            value={formData.marriageplace || ""}
                            onChange={handleChange}
                            variant="outlined"
                            placeholder="Enter marriage place"
                            sx={textInputStyle}
                          />
                        </Grid>
                      </>
                    )}

                    <Grid item xs={12} md={6}>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Economic Status
                      </label>
                      <Autocomplete
                        options={[
                          "Poor/Lower Income",
                          "Lower Middle Class",
                          "Middle Class",
                          "Upper Middle Class",
                          "Rich/Affluent",
                          "Ultra Rich/Wealthy"
                        ]}
                        value={formData.economical_status || null}
                        onChange={(e, value) =>
                          handleChange({ target: { name: "economical_status", value } })
                        }
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            placeholder="Select economic status"
                            sx={textInputStyle}
                          />
                        )}
                      />
                    </Grid>
                  </Grid>
                </div>
              </Grid>

              {/* Language Details Section */}
              <Grid item xs={12}>
                <div className="mt-6 mb-4">
                  <h3 className="text-lg font-semibold text-gray-800 mb-4 border-b-2 border-green-500 pb-2">
                    🗣️ 2. Language Details
                  </h3>
                  <Grid container spacing={3}>
                    <Grid item xs={12} md={6}>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Mother Tongue
                      </label>
                      <Autocomplete
                        options={LANGUAGES}
                        value={formData.mothertongue || null}
                        onChange={(e, value) =>
                          handleChange({ target: { name: "mothertongue", value } })
                        }
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            placeholder="Select mother tongue"
                            variant="outlined"
                            sx={textInputStyle}
                          />
                        )}
                      />
                    </Grid>

                    <Grid item xs={12} md={6}>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Languages Known
                      </label>
                      <Autocomplete
                        multiple
                        options={LANGUAGES}
                        value={
                          formData.languagesknown
                            ? typeof formData.languagesknown === "string"
                              ? formData.languagesknown.split(", ")
                              : formData.languagesknown
                            : []
                        }
                        onChange={(e, newValue) =>
                          handleChange({
                            target: { name: "languagesknown", value: newValue.join(", ") },
                          })
                        }
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            placeholder="Select languages"
                            variant="outlined"
                            sx={{
                              ...textInputStyle,
                              "& .MuiInputBase-root": {
                                height: "auto !important",
                                minHeight: "56px",
                                padding: "8px 14px",
                                alignItems: "flex-start",
                                flexWrap: "wrap",
                              },
                              "& .MuiOutlinedInput-input": {
                                padding: "0 !important",
                                minWidth: "30px",
                              },
                              "& .MuiAutocomplete-endAdornment": {
                                position: "relative",
                                top: "auto",
                                right: "auto",
                                marginTop: "4px",
                              },
                            }}
                          />
                        )}
                      />
                    </Grid>
                  </Grid>
                </div>
              </Grid>

              {/* Address Details Section */}
              <Grid item xs={12}>
                <div className="mt-6 mb-4">
                  <h3 className="text-lg font-semibold text-gray-800 mb-4 border-b-2 border-blue-500 pb-2">
                    🏠 2. Address Details
                  </h3>
                  {addressFetchError && (
                    <Typography color="error" variant="body2" sx={{ mb: 2 }}>
                      {addressFetchError}
                    </Typography>
                  )}
                  
                  {/* Current Address */}
                  <div className="mb-6">
                    <h4 className="text-md font-semibold text-gray-700 mb-3 border-l-4 border-blue-300 pl-3">
                      Current Address
                    </h4>
                    <AddressFields
                      formData={formData}
                      handleChange={handleChange}
                      addressType="Current"
                      section={1}
                      fieldErrors={{}}
                    />
                  </div>

                  {/* Permanent Address */}
                  <div className="mt-6">
                    <h4 className="text-md font-semibold text-gray-700 mb-3 border-l-4 border-blue-300 pl-3">
                      Permanent Address
                    </h4>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={sameAsCurrent}
                          onChange={(e) => {
                            const checked = e.target.checked;
                            setSameAsCurrent(checked);
                            const permanentFields = [
                              'permanent_address1',
                              'permanent_street1',
                              'permanent_pincode1',
                              'permanent_village1',
                              'permanent_taluk1',
                              'permanent_district1',
                              'permanent_state1',
                              'permanent_country1',
                            ];
                            if (checked) {
                              // Copy current address to permanent address
                              const currentFields = ['current_address1', 'current_street1', 'current_pincode1', 
                                'current_village1', 'current_taluk1', 'current_district1', 
                                'current_state1', 'current_country1'];
                              const permanentUpdates = {};
                              currentFields.forEach(field => {
                                const permanentField = field.replace('current_', 'permanent_');
                                permanentUpdates[permanentField] = formData[field] || '';
                              });
                              setFormData(prev => ({ ...prev, ...permanentUpdates }));
                            } else {
                              const clearedPermanentFields = {};
                              permanentFields.forEach(field => {
                                clearedPermanentFields[field] = '';
                              });
                              setFormData(prev => ({ ...prev, ...clearedPermanentFields }));
                            }
                          }}
                          sx={{
                            color: '#1976d2',
                            '&.Mui-checked': {
                              color: '#1976d2',
                            },
                          }}
                        />
                      }
                      label="Same as Current Address"
                      sx={{ mb: 2 }}
                    />
                    <AddressFields
                      formData={formData}
                      handleChange={handleChange}
                      addressType="Permanent"
                      section={1}
                      fieldErrors={{}}
                      isSameAsCurrent={sameAsCurrent}
                      onSameAsCurrentChange={setSameAsCurrent}
                    />
                  </div>
                </div>
              </Grid>

              {/* Education Details Section */}
              <Grid item xs={12}>
                <div className="mt-6 mb-4">
                  <h3 className="text-lg font-semibold text-gray-800 mb-4 border-b-2 border-purple-500 pb-2">
                    🎓 3. Education Details
                  </h3>
                  <div className="space-y-6">
                    {educationFetchError && (
                      <Typography color="error" variant="body2">
                        {educationFetchError}
                      </Typography>
                    )}
                    {Array.from({ length: visibleEducationSections }, (_, index) => {
                      const section = index + 1;
                      return (
                        <div
                          key={`education-section-${section}`}
                          className="border-l-4 border-green-300 pl-4"
                        >
                          <h4 className="text-md font-semibold text-gray-700 mb-3">
                            Education {section}
                          </h4>
                          {section > 1 && (
                            <Box display="flex" justifyContent="flex-end" mb={1}>
                              <Button
                                variant="contained"
                                color="error"
                                size="small"
                                onClick={() => handleRemoveEducationSection(section)}
                                sx={{
                                  textTransform: "none",
                                  fontWeight: 600,
                                  color: "#d32f2f",
                                  backgroundColor: "rgba(211,47,47,0.12)",
                                  '&:hover': {
                                    backgroundColor: "rgba(211,47,47,0.2)",
                                  },
                                }}
                              >
                                Remove
                              </Button>
                            </Box>
                          )}
                          <EducationFields
                            formData={formData}
                            handleChange={handleChange}
                            boards={boards}
                            courses={courses}
                            degreeTypes={degreeTypes}
                            institutes={institutes}
                            countries={countries}
                            states={states}
                            cities={CITIES}
                            section={section}
                            textInputStyle={textInputStyle}
                            fieldErrors={educationFieldErrors}
                          />
                        </div>
                      );
                    })}
                    {educationSectionError && (
                      <Typography color="error" variant="body2">
                        {educationSectionError}
                      </Typography>
                    )}
                    <Button
                      variant="contained"
                      color="primary"
                      onClick={handleAddEducationSection}
                      disabled={visibleEducationSections >= maxEducationSections}
                      sx={{
                        mt: 1,
                        textTransform: "none",
                        fontWeight: 600,
                        color: "#ffffff",
                        '&.Mui-disabled': {
                          color: "rgba(255,255,255,0.7)",
                        },
                      }}
                    >
                      Add Education Details
                    </Button>
                  </div>
                </div>
              </Grid>

              {/* Work Details Section */}
              <Grid item xs={12}>
                <div className="mt-6 mb-4">
                  <h3 className="text-lg font-semibold text-gray-800 mb-4 border-b-2 border-cyan-500 pb-2">
                    💼 4. Work Experience Details
                  </h3>
                  <div className="space-y-6">
                    {workFetchError && (
                      <Typography color="error" variant="body2">
                        {workFetchError}
                      </Typography>
                    )}
                    {Array.from({ length: visibleWorkSections }, (_, index) => {
                      const section = index + 1;
                      return (
                        <div
                          key={`work-section-${section}`}
                          className="border-l-4 border-cyan-300 pl-4"
                        >
                          <h4 className="text-md font-semibold text-gray-700 mb-3">
                            Work Experience {section}
                          </h4>
                          {section > 1 && (
                            <Box display="flex" justifyContent="flex-end" mb={1}>
                              <Button
                                variant="contained"
                                color="error"
                                size="small"
                                onClick={() => handleRemoveWorkSection(section)}
                                sx={{
                                  textTransform: "none",
                                  fontWeight: 600,
                                  color: "#d32f2f",
                                  backgroundColor: "rgba(211,47,47,0.12)",
                                  '&:hover': {
                                    backgroundColor: "rgba(211,47,47,0.2)",
                                  },
                                }}
                              >
                                Remove
                              </Button>
                            </Box>
                          )}
                          <WorkFields
                            formData={formData}
                            handleChange={handleChange}
                            countries={countries}
                            states={states}
                            cities={CITIES}
                            section={section}
                            textInputStyle={textInputStyle}
                            fieldErrors={workFieldErrors}
                          />
                        </div>
                      );
                    })}
                    {workSectionError && (
                      <Typography color="error" variant="body2">
                        {workSectionError}
                      </Typography>
                    )}
                    <Button
                      variant="contained"
                      color="primary"
                      onClick={handleAddWorkSection}
                      disabled={visibleWorkSections >= maxWorkSections}
                      sx={{
                        mt: 1,
                        textTransform: "none",
                        fontWeight: 600,
                        color: "#ffffff",
                        '&.Mui-disabled': {
                          color: "rgba(255,255,255,0.7)",
                        },
                      }}
                    >
                      Add Work Experience
                    </Button>
                  </div>
                </div>
              </Grid>

              {/* ID Card Details Section */}
              <Grid item xs={12}>
                <div className="mt-6 mb-4">
                  <h3 className="text-lg font-semibold text-gray-800 mb-4 border-b-2 border-purple-500 pb-2">
                    🆔 5. ID Card Details
                  </h3>
                  <div className="space-y-6">
                    {idCardFetchError && (
                      <Typography color="error" variant="body2">
                        {idCardFetchError}
                      </Typography>
                    )}
                    {Array.from({ length: visibleIDCardSections }, (_, index) => {
                      const section = index + 1;
                      return (
                        <div
                          key={`idcard-section-${section}`}
                          className="border-l-4 border-purple-300 pl-4"
                        >
                          <h4 className="text-md font-semibold text-gray-700 mb-3">
                            ID Card {section}
                          </h4>
                          {section > 1 && (
                            <Box display="flex" justifyContent="flex-end" mb={1}>
                              <Button
                                variant="contained"
                                color="error"
                                size="small"
                                onClick={() => handleRemoveIDCardSection(section)}
                                sx={{
                                  textTransform: "none",
                                  fontWeight: 600,
                                  color: "#d32f2f",
                                  backgroundColor: "rgba(211,47,47,0.12)",
                                  '&:hover': {
                                    backgroundColor: "rgba(211,47,47,0.2)",
                                  },
                                }}
                              >
                                Remove
                              </Button>
                            </Box>
                          )}
                          <IDCardFields
                            formData={formData}
                            handleChange={handleChange}
                            section={section}
                            textInputStyle={textInputStyle}
                            fieldErrors={idCardFieldErrors}
                          />
                        </div>
                      );
                    })}
                    {idCardSectionError && (
                      <Typography color="error" variant="body2">
                        {idCardSectionError}
                      </Typography>
                    )}
                    <Button
                      variant="contained"
                      color="primary"
                      onClick={handleAddIDCardSection}
                      disabled={visibleIDCardSections >= maxIDCardSections}
                      sx={{
                        mt: 1,
                        textTransform: "none",
                        fontWeight: 600,
                        color: "#ffffff",
                        '&.Mui-disabled': {
                          color: "rgba(255,255,255,0.7)",
                        },
                      }}
                    >
                      Add ID Card Details
                    </Button>
                  </div>
                </div>
              </Grid>

              {/* Bank Details Section */}
              <Grid item xs={12}>
                <div className="mt-6 mb-4">
                  <h3 className="text-lg font-semibold text-gray-800 mb-4 border-b-2 border-green-500 pb-2">
                    🏦 6. Bank Details
                  </h3>
                  <div className="space-y-6">
                    {bankFetchError && (
                      <Typography color="error" variant="body2">
                        {bankFetchError}
                      </Typography>
                    )}
                    {Array.from({ length: visibleBankSections }, (_, index) => {
                      const section = index + 1;
                      return (
                        <div
                          key={`bank-section-${section}`}
                          className="border-l-4 border-green-300 pl-4"
                        >
                          <h4 className="text-md font-semibold text-gray-700 mb-3">
                            Bank Account {section}
                          </h4>
                          {section > 1 && (
                            <Box display="flex" justifyContent="flex-end" mb={1}>
                              <Button
                                variant="contained"
                                color="error"
                                size="small"
                                onClick={() => handleRemoveBankSection(section)}
                                sx={{
                                  textTransform: "none",
                                  fontWeight: 600,
                                  color: "#d32f2f",
                                  backgroundColor: "rgba(211,47,47,0.12)",
                                  '&:hover': {
                                    backgroundColor: "rgba(211,47,47,0.2)",
                                  },
                                }}
                              >
                                Remove
                              </Button>
                            </Box>
                          )}
                          <BankDetailsFields
                            formData={formData}
                            handleChange={handleChange}
                            section={section}
                            textInputStyle={textInputStyle}
                            fieldErrors={bankFieldErrors}
                          />
                        </div>
                      );
                    })}
                    {bankSectionError && (
                      <Typography color="error" variant="body2">
                        {bankSectionError}
                      </Typography>
                    )}
                    <Button
                      variant="contained"
                      color="primary"
                      onClick={handleAddBankSection}
                      disabled={visibleBankSections >= maxBankSections}
                      sx={{
                        mt: 1,
                        textTransform: "none",
                        fontWeight: 600,
                        color: "#ffffff",
                        '&.Mui-disabled': {
                          color: "rgba(255,255,255,0.7)",
                        },
                      }}
                    >
                      Add Bank Account
                    </Button>
                  </div>
                </div>
              </Grid>

              {/* Social Links Section */}
              <Grid item xs={12}>
                <div className="mt-6 mb-4">
                  <h3 className="text-lg font-semibold text-gray-800 mb-4 border-b-2 border-purple-500 pb-2">
                    🌐 7. Social Links
                  </h3>
                  <div className="space-y-6">
                    {socialLinksFetchError && (
                      <Typography color="error" variant="body2">
                        {socialLinksFetchError}
                      </Typography>
                    )}
                    {Array.from({ length: visibleSocialLinksSections }, (_, index) => {
                      const section = index + 1;
                      return (
                        <div
                          key={`sociallinks-section-${section}`}
                          className="border-l-4 border-purple-300 pl-4"
                        >
                          <h4 className="text-md font-semibold text-gray-700 mb-3">
                            Social Link {section}
                          </h4>
                          {section > 1 && (
                            <Box display="flex" justifyContent="flex-end" mb={1}>
                              <Button
                                variant="contained"
                                color="error"
                                size="small"
                                onClick={() => handleRemoveSocialLinksSection(section)}
                                sx={{
                                  textTransform: "none",
                                  fontWeight: 600,
                                  color: "#d32f2f",
                                  backgroundColor: "rgba(211,47,47,0.12)",
                                  '&:hover': {
                                    backgroundColor: "rgba(211,47,47,0.2)",
                                  },
                                }}
                              >
                                Remove
                              </Button>
                            </Box>
                          )}
                          <SocialLinksFields
                            formData={formData}
                            handleChange={handleChange}
                            section={section}
                            textInputStyle={textInputStyle}
                            fieldErrors={socialLinksFieldErrors}
                          />
                        </div>
                      );
                    })}
                    {socialLinksSectionError && (
                      <Typography color="error" variant="body2">
                        {socialLinksSectionError}
                      </Typography>
                    )}
                    <Button
                      variant="contained"
                      color="primary"
                      onClick={handleAddSocialLinksSection}
                      disabled={visibleSocialLinksSections >= maxSocialLinksSections}
                      sx={{
                        mt: 1,
                        textTransform: "none",
                        fontWeight: 600,
                        color: "#ffffff",
                        '&.Mui-disabled': {
                          color: "rgba(255,255,255,0.7)",
                        },
                      }}
                    >
                      Add Social Link
                    </Button>
                  </div>
                </div>
              </Grid>

              {/* About You */}
              <Grid item xs={12}>
                <label className="block text-sm font-medium text-gray-700 mb-1">About You</label>
                <TextField
                  fullWidth
                  name="description"
                  value={formData.description || ""}
                  onChange={handleChange}
                  variant="outlined"
                  multiline
                  rows={4}
                  placeholder="Tell us about yourself..."
                />
              </Grid>
            </Grid>
          </form>
        </DialogContent>

        <DialogActions sx={{ p: 3, pt: 0 }}>
          <Button
            onClick={onClose}
            disabled={isSubmitting}
            variant="outlined"
            sx={{
              minWidth: 100,
              color: "#d32f2f",
              borderColor: "#d32f2f",
              fontWeight: 600,
              "&:hover": {
                borderColor: "#c62828",
                backgroundColor: "rgba(211, 47, 47, 0.04)",
              },
              "&.Mui-disabled": {
                color: "rgba(0, 0, 0, 0.26)",
                borderColor: "rgba(0, 0, 0, 0.12)",
              },
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={isSubmitting || casteInvalid || religionInvalid}
            variant="contained"
            color="primary"
            sx={{ minWidth: 100, color: "#fff", fontWeight: 600 }}
          >
            {isSubmitting ? "Saving..." : "Save Changes"}
          </Button>
        </DialogActions>
      </Dialog>

      <AddMasterDataDialog
        open={addDialog.open}
        onClose={closeAddMasterDialog}
        category={addDialog.category}
        initialValue={addDialog.initialValue}
        sessionId={sessionId}
        onSuccess={handleAddMasterSuccess}
      />

      {/* Camera Dialog */}
      <Dialog open={cameraDialogOpen} onClose={handleCloseCamera} maxWidth="sm" fullWidth>
        <DialogTitle className="flex justify-between items-center">
          <span>📷 Capture Photo</span>
          <IconButton onClick={handleCloseCamera}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Box className="flex flex-col items-center gap-4">
            <Webcam
              audio={false}
              ref={webcamRef}
              screenshotFormat="image/jpeg"
              width="100%"
              videoConstraints={{
                facingMode: "user",
              }}
            />
            <Button
              variant="contained"
              color="primary"
              onClick={handleCapturePhoto}
              startIcon={<CameraAltIcon />}
              fullWidth
              sx={{ color: "#fff" }}
            >
              Capture Photo
            </Button>
          </Box>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default EditProfileDialog;
